import type { FormationId, TacticalPresetId } from "../domain/club";

export type TeamId = "home" | "away";
export type MatchMentality = "defensive" | "balanced" | "attacking";
export type MatchPhase =
  | "presentation"
  | "first-half"
  | "halftime"
  | "second-half"
  | "fulltime";
export type PlayerRole = "GK" | "LB" | "CB" | "RB" | "CM" | "LW" | "RW" | "ST";
export type PlayerAction = "idle" | "run" | "kick" | "tackle" | "skill" | "celebrate";

export interface MatchLineupPlayer {
  profileId: string;
  displayName: string;
  number: number;
}

export interface MatchConfig {
  id: string;
  homeName: string;
  homeShortName: string;
  awayName: string;
  awayShortName: string;
  homeColor: string;
  awayColor: string;
  difficulty: "beginner" | "amateur" | "professional";
  realDurationSeconds: number;
  createdAt: string;
  homeFormation?: FormationId;
  awayFormation?: FormationId;
  homeTacticalPreset?: TacticalPresetId;
  homeLineup?: MatchLineupPlayer[];
  homeBench?: MatchLineupPlayer[];
}

export interface PlayerState {
  id: string;
  team: TeamId;
  shirtNumber: number;
  role: PlayerRole;
  profileId?: string;
  displayName?: string;
  x: number;
  z: number;
  vx: number;
  vz: number;
  heading: number;
  stamina: number;
  action: PlayerAction;
  actionNonce: number;
}

export interface BallState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  angularVelocityX: number;
  angularVelocityZ: number;
  controlledBy: string | null;
  lastTouch: string | null;
}

export interface MatchStats {
  homeShots: number;
  awayShots: number;
  homeShotsOnTarget: number;
  awayShotsOnTarget: number;
  homePossessionTicks: number;
  awayPossessionTicks: number;
  homePasses: number;
  awayPasses: number;
  homeTackles: number;
  awayTackles: number;
}

export interface MatchEvent {
  id: number;
  minute: number;
  kind:
    | "kickoff"
    | "pass"
    | "through"
    | "shot"
    | "goal"
    | "tackle"
    | "skill"
    | "substitution"
    | "tactics"
    | "halftime"
    | "fulltime";
  team: TeamId | null;
  playerId: string | null;
  label: string;
}

export interface SubstitutionRecord {
  minute: number;
  outProfileId: string;
  outName: string;
  inProfileId: string;
  inName: string;
  slotId: string;
}

export interface MatchSnapshot {
  schemaVersion: 1;
  config: MatchConfig;
  phase: MatchPhase;
  clockSeconds: number;
  homeScore: number;
  awayScore: number;
  players: PlayerState[];
  ball: BallState;
  activePlayerId: string;
  stats: MatchStats;
  events: MatchEvent[];
  eventNonce: number;
  mentality: MatchMentality;
  substitutions: SubstitutionRecord[];
}

export interface MatchInput {
  moveX: number;
  moveZ: number;
  sprint: boolean;
  pass: boolean;
  through: boolean;
  shoot: boolean;
  tackle: boolean;
  slide: boolean;
  secondDefender: boolean;
  skill: boolean;
  switchPlayer: boolean;
  passPower: number;
  throughPower: number;
  shotPower: number;
}

export interface MatchSummary {
  id: string;
  playedAt: string;
  homeName: string;
  awayName: string;
  homeScore: number;
  awayScore: number;
  result: "win" | "draw" | "loss";
  stats: MatchStats;
}

export const EMPTY_INPUT: MatchInput = {
  moveX: 0,
  moveZ: 0,
  sprint: false,
  pass: false,
  through: false,
  shoot: false,
  tackle: false,
  slide: false,
  secondDefender: false,
  skill: false,
  switchPlayer: false,
  passPower: 0.42,
  throughPower: 0.5,
  shotPower: 0.55
};

export const PITCH = {
  length: 105,
  width: 68,
  halfLength: 52.5,
  halfWidth: 34,
  goalWidth: 7.32,
  goalHeight: 2.44
} as const;
